import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

const faqs = [
  {
    question: '黄游戏是什么？',
    answer: '黄游戏是一个专注于中文游戏资讯与攻略推荐的门户平台。我们汇聚最新热门游戏排行榜、深度玩法攻略、专业评测与下载推荐，致力于为中文玩家提供最优质的游戏内容服务。',
  },
  {
    question: '网站的内容多久更新一次？',
    answer: '我们的编辑团队每日更新游戏资讯和攻略内容。重大游戏版本更新、新游戏上线等热点事件，我们会在24小时内产出相关内容，确保玩家能够第一时间获取最新信息。',
  },
  {
    question: '我可以投稿攻略或评测吗？',
    answer: '当然可以！我们欢迎所有热爱游戏的玩家投稿原创攻略和评测。请通过本页的联系表单提交您的稿件，我们的编辑团队会在3个工作日内审核并回复。',
  },
  {
    question: '网站上的游戏可以下载吗？',
    answer: '黄游戏主要提供游戏资讯、攻略和评测内容。我们不直接提供游戏下载，但会在文章中标注游戏在各平台（App Store、Google Play、官网等）的下载方式，方便玩家获取。',
  },
  {
    question: '如何联系我们进行商务合作？',
    answer: '商务合作请通过本页底部的联系表单提交具体合作意向，我们的商务团队会在收到信息后尽快与您取得联系。',
  },
];

const testimonials = [
  {
    name: '游戏玩家小陈',
    role: '资深手游玩家',
    content: '黄游戏的攻略质量真的高！每次新版本更新我都第一时间来这里看攻略，写得详细又实用，帮我少走了很多弯路。',
    rating: 5,
  },
  {
    name: '电竞爱好者阿强',
    role: '王者荣耀宗师段位',
    content: '赛季更新就来看英雄排行，推荐的上分英雄真的很好用。现在已经是我的每日必刷网站了。',
    rating: 5,
  },
  {
    name: '二次元游戏迷',
    role: '原神&星穹铁道玩家',
    content: '二游攻略做得特别用心，配队推荐和圣遗物选择都分析得很透彻。每次都期待新文章的推送！',
    rating: 5,
  },
  {
    name: '休闲玩家Lucy',
    role: '蛋仔派对爱好者',
    content: '蛋仔的攻略特别全，从入门到进阶都有，跟着攻略练了几天技术提升了好多，感谢！',
    rating: 4,
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('您的信息已提交，感谢联系！');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qunmcnlsngrm4lm6dg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的联系！我们会尽快回复。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]}
      />

      <section className="mb-16">
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 font-bold mb-4">
            关于黄游戏
          </h1>
          <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            中文游戏玩家的资讯攻略枢纽
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
              <i className="ri-eye-line text-xl"></i>
            </div>
            <h3 className="font-heading text-lg text-foreground-900 font-bold mb-2">我们的愿景</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">
              成为中文游戏玩家最信赖的资讯与攻略平台，让每一位玩家都能轻松找到适合自己的游戏。
            </p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
              <i className="ri-team-line text-xl"></i>
            </div>
            <h3 className="font-heading text-lg text-foreground-900 font-bold mb-2">我们的团队</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">
              由资深游戏玩家和专业编辑组成的团队，每日产出高质量的攻略、评测和资讯内容。
            </p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
              <i className="ri-heart-line text-xl"></i>
            </div>
            <h3 className="font-heading text-lg text-foreground-900 font-bold mb-2">我们的承诺</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">
              坚持原创内容，拒绝低质搬运。每一篇攻略都经过实测验证，确保内容的准确性和实用性。
            </p>
          </div>
        </div>

        <div className="bg-background-50 rounded-xl border border-background-200/70 p-8">
          <h2 className="font-heading text-xl text-foreground-900 font-bold mb-4">品牌故事</h2>
          <div className="text-sm text-foreground-600 leading-loose space-y-3">
            <p>
              黄游戏成立于2024年，由一群热爱游戏的玩家共同创建。创始人发现，市面上虽然游戏媒体众多，但真正站在<i>玩家角度</i>、提供<strong>实用攻略和深度评测</strong>的平台却寥寥无几。于是，黄游戏应运而生。
            </p>
            <p>
              从最初几个人的小团队，到现在拥有完整的编辑部和内容运营体系，我们始终坚持<strong>「玩家至上」</strong>的理念。每一篇攻略都经过多轮实测校对，每一份评测都力求客观公正。
            </p>
            <p>
              目前，黄游戏已覆盖<strong>角色扮演、动作冒险、MOBA、射击、休闲</strong>等多个游戏品类，累计产出数千篇高质量内容，服务数百万中文玩家。未来，我们将继续深耕游戏内容领域，为玩家带来更丰富、更专业的游戏资讯与攻略服务。
            </p>
          </div>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="font-heading text-2xl text-foreground-900 font-bold mb-8 text-center">
          玩家口碑
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t) => (
            <div
              key={t.name}
              className="bg-background-50 rounded-xl border border-background-200/70 p-6"
            >
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`text-sm ${i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                「{t.content}」
              </p>
              <div>
                <p className="text-sm font-semibold text-foreground-800">{t.name}</p>
                <p className="text-xs text-foreground-400">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-16">
        <h2 className="font-heading text-2xl text-foreground-900 font-bold mb-8 text-center">
          常见问题
        </h2>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <details
              key={index}
              className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer"
            >
              <summary className="flex items-center justify-between p-5 font-medium text-foreground-800 text-sm list-none marker:hidden">
                {faq.question}
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-5 pb-5 text-sm text-foreground-500 leading-relaxed">
                {faq.answer}
              </div>
            </details>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-2xl text-foreground-900 font-bold mb-8 text-center">
          联系我们
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
            <h3 className="font-heading text-lg text-foreground-900 font-bold mb-4">联系方式</h3>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-mail-line"></i>
                </div>
                <div>
                  <p className="font-medium text-foreground-800">电子邮箱</p>
                  <p className="text-foreground-500">contact@huangyouxi.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-wechat-line"></i>
                </div>
                <div>
                  <p className="font-medium text-foreground-800">微信公众号</p>
                  <p className="text-foreground-500">黄游戏官方</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                  <i className="ri-bilibili-line"></i>
                </div>
                <div>
                  <p className="font-medium text-foreground-800">B站账号</p>
                  <p className="text-foreground-500">黄游戏官方</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
            <h3 className="font-heading text-lg text-foreground-900 font-bold mb-4">发送消息</h3>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                  您的姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                  电子邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                  消息内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的消息（500字以内）"
                ></textarea>
              </div>

              <div className="spam-guard">
                <label htmlFor="phone_alt">备用电话</label>
                <input
                  type="text"
                  id="phone_alt"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formStatus === 'success' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-accent-50 text-accent-700 text-sm">
                  <i className="ri-check-line"></i>
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                  <i className="ri-error-warning-line"></i>
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 font-medium rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    发送中...
                  </span>
                ) : (
                  '发送消息'
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}